// Default configuration + preset library.

export const DEFAULT_CONFIG = {
  seed: 1,
  tickMs: 100, // sim ms per tick
  world: {
    userCount: 1,
    returnProb: 1.0,
    diurnalAmplitude: 0.0,
  },
  assets: {
    count: 1,
    sizeLognormalMu: 10.5,  // ~36 KB median
    sizeLognormalSigma: 1.2,
    churnMin: 0.15,
    churnMax: 0.25,
    modIntervalMeanS: 60,
    modIntervalJitter: 0.5,
    staticCompressionRatio: 0.35, // full response = size * ratio (br/zstd baseline)
  },
  traffic: {
    model: 'manual', // 'manual' | 'poisson' | 'zipf' | 'trace'
    poisson: { perAssetLambdaPerSec: 0.4 },
    zipf: { s: 1.07, totalLambdaPerSec: 3.5 },
    trace: { json: '' }
  },
  delta: {
    alpha: 0.35,          // decay for version distance
    overheadBytes: 128,
    minRatio: 0.005,
    maxRatio: 0.9,
  },
  server: {
    unifiedPool: false,
    capacityBytes: 8 * 1024 * 1024,           // 8 MB unified
    capacityBytesByKind: {
      asset: 6 * 1024 * 1024,
      dictionary: 2 * 1024 * 1024,
      delta: 2 * 1024 * 1024,
    },
    ttlSec: { asset: 3600, dictionary: 7200, delta: 1800 },
    pinHitThreshold: 25,
    pinWindowSec: 900,
    deltaRegenCostMs: 40,
  },
  client: {
    perUserCacheBytes: 512 * 1024,
    // Outer eviction bound: how long an entry can live in cache at all.
    ttlSec: { asset: 600, dictionary: 3600 },
    // Freshness window: while (now - storedAt) < maxAge, the response is served
    // from cache with no network request. After that, the client revalidates
    // with If-None-Match (and Available-Dictionary if applicable).
    maxAgeSec: { asset: 10, dictionary: 3600 },
    revalidationBytesOverhead: 200,
  },
  failures: {
    forceHashMismatchProb: 0.0,
    forceUnsupportedEncodingProb: 0.02,
  },
  graph: { windowPerAsset: 12 }
};

export const PRESETS = {
  'manual-1x1': {
    label: 'Manual (1 asset, 1 user)',
    patch: {
      world: { userCount: 1, returnProb: 1.0, diurnalAmplitude: 0.0 },
      assets: { count: 1, churnMin: 0.15, churnMax: 0.25, modIntervalMeanS: 60 },
      traffic: { model: 'manual' },
      client: { ttlSec: { asset: 600, dictionary: 3600 }, maxAgeSec: { asset: 10, dictionary: 3600 } },
    }
  },
  'static-site-shared-js': {
    label: 'Static site w/ shared JS bundle',
    patch: {
      assets: {
        count: 3,
        sizeLognormalMu: 12.4,   // ~240 KB
        sizeLognormalSigma: 0.6,
        churnMin: 0.02,
        churnMax: 0.08,
        modIntervalMeanS: 3600 * 6,  // updates 4x/day
        modIntervalJitter: 0.6,
      },
      traffic: {
        model: 'zipf',
        zipf: { s: 1.2, totalLambdaPerSec: 6 }
      },
      server: { capacityBytes: 16 * 1024 * 1024, pinHitThreshold: 15 }
    }
  },
  'frequent-json-api': {
    label: 'Frequently updated JSON API',
    patch: {
      assets: {
        count: 8,
        sizeLognormalMu: 9.2,   // ~10 KB
        sizeLognormalSigma: 0.9,
        churnMin: 0.2,
        churnMax: 0.6,
        modIntervalMeanS: 120,
        modIntervalJitter: 0.4,
      },
      traffic: {
        model: 'poisson',
        poisson: { perAssetLambdaPerSec: 1.2 }
      },
      delta: { alpha: 0.6 }
    }
  },
  'huge-wasm-binary': {
    label: 'Huge WASM binary',
    patch: {
      assets: {
        count: 2,
        sizeLognormalMu: 14.9,   // ~3 MB
        sizeLognormalSigma: 0.3,
        churnMin: 0.01,
        churnMax: 0.05,
        modIntervalMeanS: 3600 * 24 * 2, // every ~2 days
      },
      traffic: {
        model: 'poisson',
        poisson: { perAssetLambdaPerSec: 0.15 }
      },
      server: {
        capacityBytes: 64 * 1024 * 1024,
        capacityBytesByKind: {
          asset: 32 * 1024 * 1024,
          dictionary: 16 * 1024 * 1024,
          delta: 16 * 1024 * 1024,
        },
      },
      client: { perUserCacheBytes: 8 * 1024 * 1024 }
    }
  }
};

// Deep merge (small util, patches are shallow-ish objects).
export function applyPreset(cfg, presetKey) {
  const p = PRESETS[presetKey];
  if (!p) return cfg;
  return deepMerge(cfg, p.patch);
}

export function deepMerge(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) return b ?? a;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return b ?? a;
  const out = { ...a };
  for (const k of Object.keys(b)) out[k] = deepMerge(a[k], b[k]);
  return out;
}
